// İşletme ağacı saf yardımcıları (KPI spec Rev 2 K16/C9) — backend resolve konsolidasyonu ve
// FE ağaç görünümü aynı fonksiyonları kullanır. Kök = müşteri (satır yok); satırlar `parentId`
// ile ağaç kurar. Ara toplam = ara düğümün aktif yaprakları, konsolide = tüm aktif yapraklar.
// TODO-KPI-11 — dönem bazlı üyelik: `validFrom`/`validTo` (yıl, dahil; null = sınırsız) ile bir
// işletme yalnız o yıl aralığında "aktif" sayılır. `buildEntityTree(rows, year)` yıl verildiğinde
// yaprak seçimi yıl bazlıdır; `leaves(nodeId, year)` / `activeLeaves(year)` çağrı başına da yıl
// alır. Yıl verilmezse yalnız `isActive` bakılır (eski davranış byte-eşit).

export interface EntityTreeRow {
  id: string;
  parentId: string | null;
  isActive: boolean;
  orderIndex: number;
  name: string;
  /** Dahil olduğu ilk yıl (dahil); null/yok = sınırsız. */
  validFrom?: number | null | undefined;
  /** Dahil olduğu son yıl (dahil); null/yok = sınırsız. */
  validTo?: number | null | undefined;
}

export interface EntityNode<T extends EntityTreeRow = EntityTreeRow> {
  row: T;
  children: EntityNode<T>[];
  depth: number;
}

export interface EntityTree<T extends EntityTreeRow = EntityTreeRow> {
  roots: EntityNode<T>[];
  byId: Map<string, EntityNode<T>>;
  /** Düğümün (kendisi dahil) alt ağacındaki AKTİF yaprak satırları (yıl verilirse o yıl için). */
  leaves(nodeId: string, year?: number): T[];
  /** Tüm aktif yapraklar (konsolide tabanı; yıl verilirse o yılda dahil olanlar). */
  activeLeaves(year?: number): T[];
  /** Kökten düğüme yol (kök önce). */
  pathTo(nodeId: string): T[];
  /** `candidateParentId`, `nodeId`'nin kendisi ya da alt ağacındaysa true (döngü). */
  wouldCycle(nodeId: string, candidateParentId: string | null): boolean;
  /** Ön sıralı (depth-first, orderIndex sırasıyla) düz liste. */
  flatten(): EntityNode<T>[];
}

/**
 * İşletme verilen yılda gruba/konsolideye dahil mi? `isActive=false` her yılda hariç;
 * `validFrom`/`validTo` null ise o uç sınırsız. `year` verilmezse yalnız `isActive`.
 */
export function isEntityActiveInYear(
  row: Pick<EntityTreeRow, "isActive" | "validFrom" | "validTo">,
  year?: number,
): boolean {
  if (!row.isActive) return false;
  if (year === undefined) return true;
  if (row.validFrom !== null && row.validFrom !== undefined && year < row.validFrom) return false;
  if (row.validTo !== null && row.validTo !== undefined && year > row.validTo) return false;
  return true;
}

/** Rozet metni: "2023–2025" · "2024–" · "–2022" · null (sınırsız). */
export function formatEntityValidity(
  row: Pick<EntityTreeRow, "validFrom" | "validTo">,
): string | null {
  const from = row.validFrom ?? null;
  const to = row.validTo ?? null;
  if (from === null && to === null) return null;
  return `${from === null ? "" : String(from)}–${to === null ? "" : String(to)}`;
}

function sortRows<T extends EntityTreeRow>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) => a.orderIndex - b.orderIndex || a.name.localeCompare(b.name, "tr"),
  );
}

/**
 * Düz satır listesinden ağaç kurar. Üst düğümü listede olmayan satır kök sayılır (yetim koruması).
 * Pasif bir düğümün alt ağacı da konsolideye girmez (C9: pasif işletme yeni girişten ve toplamdan
 * düşer); yıl verildiğinde o yılda dahil olmayan düğümün alt ağacı da aynı şekilde dışarıda kalır.
 */
export function buildEntityTree<T extends EntityTreeRow>(rows: T[], year?: number): EntityTree<T> {
  const byId = new Map<string, EntityNode<T>>();
  for (const row of rows) byId.set(row.id, { row, children: [], depth: 0 });
  const roots: EntityNode<T>[] = [];
  for (const row of sortRows(rows)) {
    const node = byId.get(row.id)!;
    const parent = row.parentId ? byId.get(row.parentId) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  // Derinlik (kök 0) — pre-order.
  const stack: EntityNode<T>[] = [...roots];
  while (stack.length) {
    const n = stack.pop()!;
    for (const c of n.children) {
      c.depth = n.depth + 1;
      stack.push(c);
    }
  }

  function collectLeaves(node: EntityNode<T>, out: T[], y: number | undefined): void {
    if (!isEntityActiveInYear(node.row, y)) return;
    if (node.children.length === 0) {
      out.push(node.row);
      return;
    }
    for (const c of node.children) collectLeaves(c, out, y);
  }

  const tree: EntityTree<T> = {
    roots,
    byId,
    leaves(nodeId, y = year) {
      const node = byId.get(nodeId);
      if (!node) return [];
      const out: T[] = [];
      collectLeaves(node, out, y);
      return out;
    },
    activeLeaves(y = year) {
      const out: T[] = [];
      for (const r of roots) collectLeaves(r, out, y);
      return out;
    },
    pathTo(nodeId) {
      const path: T[] = [];
      let cur = byId.get(nodeId);
      const seen = new Set<string>();
      while (cur && !seen.has(cur.row.id)) {
        seen.add(cur.row.id);
        path.unshift(cur.row);
        cur = cur.row.parentId ? byId.get(cur.row.parentId) : undefined;
      }
      return path;
    },
    wouldCycle(nodeId, candidateParentId) {
      if (!candidateParentId) return false;
      if (candidateParentId === nodeId) return true;
      let cur = byId.get(candidateParentId);
      const seen = new Set<string>();
      while (cur && !seen.has(cur.row.id)) {
        if (cur.row.id === nodeId) return true;
        seen.add(cur.row.id);
        cur = cur.row.parentId ? byId.get(cur.row.parentId) : undefined;
      }
      return false;
    },
    flatten() {
      const out: EntityNode<T>[] = [];
      const visit = (n: EntityNode<T>): void => {
        out.push(n);
        for (const c of n.children) visit(c);
      };
      for (const r of roots) visit(r);
      return out;
    },
  };
  return tree;
}

/** Ağaçta birden çok aktif yaprak varsa müşteri "grup" sayılır (K27 çift geometri kapısı). */
export function isGroupClient(rows: EntityTreeRow[], year?: number): boolean {
  return buildEntityTree(rows, year).activeLeaves().length > 1;
}
